/**
 * API Helper Utilities
 *
 * File-level:
 * - Centralized API base resolution and fetch wrapper for frontend code.
 * - Provides runtime override (window.__API_BASE__) and a safe default.
 * - Exposes fetchJSON<T> for typed JSON requests and apiBase() for raw URL construction.
 *
 * Usage:
 * - import { apiBase, fetchJSON, setRuntimeApiBase } from '@/utils/api';
 *
 * Notes:
 * - This file uses TypeScript generics for typed responses.
 * - All functions include JSDoc comments as required.
 */

import { API_CONFIG } from '../config/api.config';

/**
 * Runtime container on window used for overriding API base in dev tools
 * (window.__API_BASE__ = 'http://localhost:5000/api')
 */
declare global {
  interface Window {
    __API_BASE__?: string;
  }
}

/**
 * apiBase
 *
 * @description
 * - Resolves the API base URL using centralized configuration
 * - Priority: window.__API_BASE__ > API_CONFIG
 *
 * @returns {string} The resolved API base URL (no trailing slash).
 */
export function apiBase(): string {
  if (typeof window === 'undefined') return '/api';
  const runtime = window.__API_BASE__;
  if (runtime && String(runtime).trim().length > 0) return String(runtime).replace(/\/+$/g, '');
  return API_CONFIG.getApiBaseUrl().replace(/\/+$/g, '');
}

/**
 * setRuntimeApiBase
 *
 * @description Sets the runtime API base override (useful for debugging in browser console)
 * @param {string} url - full API base (e.g. 'http://localhost:5000/api')
 */
export function setRuntimeApiBase(url: string) {
  if (typeof window === 'undefined') return;
  if (!url) {
    // clear override
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__API_BASE__ = undefined;
    delete (window as any).__API_BASE__;
    return;
  }
  // ensure no trailing slash
  (window as any).__API_BASE__ = String(url).replace(/\/+$/g, '');
}

/**
 * normalizeUrl
 *
 * @description Ensures a path gets converted to an absolute URL using apiBase.
 * If an absolute URL is provided already (http/https) it is returned untouched.
 *
 * This function also avoids duplicating the "/api" segment when apiBase already ends
 * with "/api" and the provided path begins with "/api". Example problematic case:
 *  - apiBase() -> "http://localhost:5000/api"
 *  - path -> "/api/chat/unread-senders/..."
 *  Without handling, concatenation yields ".../api/api/..."
 *
 * @param {string} pathOrUrl - API path (e.g. '/dashboard/..') or full URL
 * @returns {string} Absolute URL to fetch
 */
function normalizeUrl(pathOrUrl: string): string {
  if (!pathOrUrl) throw new Error('normalizeUrl: pathOrUrl is required');
  const s = String(pathOrUrl).trim();
  if (/^https?:\/\//i.test(s)) return s;

  const base = apiBase();
  // ensure path begins with a single slash
  let p = s.startsWith('/') ? s : `/${s}`;

  /**
   * If base already ends with '/api' and the path starts with '/api' then strip the leading
   * '/api' segment from the path so the final URL is not duplicated.
   *
   * Cases handled:
   *  - base: http://host:port/api
   *    path: /api/chat/...
   *    => final: http://host:port/api/chat/...
   *
   *  - base: http://host:port/api
   *    path: /users/...  (no change)
   */
  try {
    if (base.toLowerCase().endsWith('/api') && p.toLowerCase().startsWith('/api/')) {
      p = p.replace(/^\/api/i, ''); // remove leading "/api"
      if (!p.startsWith('/')) p = `/${p}`; // ensure we still have a leading slash
    } else if (base.toLowerCase().endsWith('/api') && p.toLowerCase() === '/api') {
      // path is exactly '/api' and base ends with '/api' -> avoid double '/api'
      p = '';
    }
  } catch {
    // fallback: do nothing on error and allow normal concatenation
  }

  return `${base}${p}`;
}

/**
 * fetchJSON
 *
 * @description A small typed wrapper around fetch that:
 *  - prefixes relative paths with apiBase
 *  - sets Content-Type: application/json by default
 *  - parses JSON responses and throws on non-ok responses
 *
 * @template T - expected JSON response type
 * @param {string} pathOrUrl - relative API path or full URL
 * @param {RequestInit} options - fetch options (method, body, headers, etc.)
 * @returns {Promise<T>} parsed JSON body
 *
 * @throws {Error} with message including status when response.ok === false
 */
export async function fetchJSON<T = any>(pathOrUrl: string, options: RequestInit = {}): Promise<T> {
  const url = normalizeUrl(pathOrUrl);

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };

  const opts: RequestInit = {
    ...options,
    headers,
  };

  // If a body is present and is an object, stringify it (unless already a string or FormData)
  if (opts.body && typeof opts.body === 'object' && !(opts.body instanceof FormData)) {
    try {
      opts.body = JSON.stringify(opts.body);
    } catch {
      // leave as-is
    }
  }

  const res = await fetch(url, opts);

  // Attempt to parse JSON safely
  const tryParseJson = async () => {
    try {
      const text = await res.text();
      if (!text) return null;
      return JSON.parse(text);
    } catch {
      return null;
    }
  };

  if (!res.ok) {
    // Try to include parsed JSON error message if available
    const errBody = await tryParseJson();
    const message = errBody && typeof errBody === 'object' && (errBody as any).message
      ? `${res.status} ${res.statusText} - ${String((errBody as any).message)}`
      : `${res.status} ${res.statusText}`;

    const err = new Error(message);
    // attach raw body for debugging
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (err as any).body = errBody;
    (err as any).status = res.status;
    throw err;
  }

  // For successful responses, parse JSON if possible
  const body = await tryParseJson();
  return body as T;
}

export default {
  apiBase,
  fetchJSON,
  setRuntimeApiBase,
};